export const normalizeEmployeeIdForQuery = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (/^\d+$/.test(raw)) {
    const normalized = raw.replace(/^0+(?=\d)/, '');
    return normalized || '0';
  }
  return raw;
};

export const buildEmployeeIdCandidates = (values) => {
  return Array.from(new Set((Array.isArray(values) ? values : []).map(normalizeEmployeeIdForQuery).filter(Boolean)));
};

export const normalizeRecordISODate = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${String(m[2]).padStart(2, '0')}-${String(m[3]).padStart(2, '0')}`;
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    const y = d.getFullYear();
    const mm = d.getMonth() + 1;
    const dd = d.getDate();
    return `${y}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
  }
  return '';
};

export const computeMonthWindowFromEndISO = (endISO) => {
  const endObj = new Date(`${String(endISO).slice(0, 10)}T00:00:00+08:00`);
  const y = endObj.getFullYear();
  const m = endObj.getMonth() + 1;
  const lastDay = new Date(y, m, 0).getDate();
  const monthStartISO = `${y}-${String(m).padStart(2, '0')}-01`;
  const monthEndISO = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { monthStartISO, monthEndISO };
};

export const createMonthlyGrossDebugger = ({ sessionId, url }) => {
  const sid = sessionId || 'monthly-gross-prev-gross-zero';
  const eventUrl = url || '';

  return (payload) => {
    try {
      if (typeof window !== 'undefined') {
        const evt = { sessionId: sid, ts: Date.now(), ...payload };
        const arr = Array.isArray(window.__TRAE_MG_DEBUG_EVENTS__) ? window.__TRAE_MG_DEBUG_EVENTS__ : [];
        const next = arr.length > 200 ? arr.slice(arr.length - 200) : arr.slice();
        next.push(evt);
        window.__TRAE_MG_DEBUG_EVENTS__ = next;
        window.__TRAE_MG_DEBUG_LAST__ = evt;
      }
      if (typeof process !== 'undefined' && process?.env?.NODE_ENV === 'test') return;
      if (!eventUrl) return;
      if (typeof window === 'undefined' || typeof fetch !== 'function') return;
      fetch(eventUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sid, ...payload })
      }).catch(() => {});
    } catch {}
  };
};

export const fetchPayrollRecordsByEmployeeIdCandidates = async ({ axiosInstance, employeeIdCandidates, endDateISO }) => {
  const attempts = [];
  let records = [];
  let usedEmployeeId = '';

  for (const cand of employeeIdCandidates) {
    const params = new URLSearchParams({ employee_id: String(cand), end_date: String(endDateISO) });
    try {
      const resp = await axiosInstance.get(`/payroll/records?${params.toString()}`);
      const dataIsArray = Array.isArray(resp.data);
      const count = dataIsArray ? resp.data.length : 0;
      attempts.push({
        employee_id: String(cand),
        status: resp.status,
        dataType: dataIsArray ? 'array' : (resp.data == null ? 'null' : typeof resp.data),
        count
      });
      if (dataIsArray && count > 0) {
        usedEmployeeId = String(cand);
        records = resp.data;
        break;
      }
    } catch (e) {
      attempts.push({
        employee_id: String(cand),
        status: e?.response?.status || 0,
        dataType: 'error',
        count: 0
      });
    }
  }

  return { attempts, usedEmployeeId, records };
};

export const computePrevGrossAndGovFromMonthlyPayrollRecords = ({ records, monthStartISO, monthEndISO, selectedStartISO, selectedEndISO }) => {
  const normalized = (Array.isArray(records) ? records : [])
    .map((r) => ({
      ...r,
      _startISO: normalizeRecordISODate(r?.start_date),
      _endISO: normalizeRecordISODate(r?.end_date)
    }))
    .filter((r) => r._startISO && r._endISO);

  const latestByPeriod = {};
  normalized.forEach((r) => {
    const key = `${r._startISO}_${r._endISO}`;
    const existing = latestByPeriod[key];
    if (!existing || new Date(r.created_at || 0) > new Date(existing.created_at || 0)) {
      latestByPeriod[key] = r;
    }
  });

  const deduped = Object.values(latestByPeriod);
  const earlierInMonth = deduped.filter((r) => {
    const red = r?._endISO;
    if (!red) return false;
    if (red < monthStartISO || red > monthEndISO) return false;
    if (String(r?._startISO || '').trim() === String(selectedStartISO).trim() && String(r?._endISO || '').trim() === String(selectedEndISO).trim()) return false;
    return red < selectedStartISO;
  });

  const prevGross = earlierInMonth.reduce((sum, r) => sum + (parseFloat(r?.gross_salary) || 0), 0);
  const prevGov = earlierInMonth.reduce((sum, r) => {
    const hdmf = parseFloat(r?.hdmf_contribution) || 0;
    const phic = parseFloat(r?.philhealth_contribution) || 0;
    const sss = parseFloat(r?.sss_contribution) || 0;
    return sum + hdmf + phic + sss;
  }, 0);

  return { prevGross, prevGov, deduped, earlierInMonth, normalizedCount: normalized.length };
};
